import React, { useCallback, useMemo, useState } from 'react';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { styled } from '@mui/material/styles';
import { Modal } from 'apps/sigma2-ui/src/components/ui/Modal';
import { OptimizingCardTopFactor } from 'apps/sigma2-ui/src/components/key-factors/optimizing-card/OptimizingCardTopFactor';
import { KeyFactorsGradedImpactDataPoint } from 'apps/sigma2-ui/src/components/key-factors/types';
import { KeyFactorsImpactDataPoint } from 'libs/end-to-end/src/success-phenotype';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { getControlVariableDefinitionsMapping } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { OptimizingCardSerializableProps } from 'libs/end-to-end/src/success-phenotype';
import { getBreakdownExplanationForKeyFactors } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { getTargetRangeDisplayForKeyFactors } from 'libs/end-to-end/src/success-phenotype-v2-utils';

const BorderlessAccordion = styled(Accordion)(() => ({
  '&.MuiAccordion-root:before': {
    display: 'none',
  },
}));

export interface OptimizingCardProps extends OptimizingCardSerializableProps {
  handleSelectTrait: (controlVariableColumnHeaderValue: string) => void;
  setIsTraitHovered: (index: number, next: boolean) => void;
  expandAllTraitsForReport?: boolean;
}

// eslint-disable-next-line complexity
export const OptimizingCard: React.FC<OptimizingCardProps> = ({
  metricColumnHeaderValue,
  dataPoints,
  handleSelectTrait,
  setIsTraitHovered,
  explanations,
  traits,
  errorStateReason,
  expandAllTraitsForReport,
}) => {
  const language = useLanguage();

  const { myObservation } = useObservation();

  const gradedImpactDataPoint: KeyFactorsGradedImpactDataPoint[] = useMemo(
    () =>
      dataPoints
        .sort((a, b) => b.impact - a.impact)
        .map((d) => ({
          ...d,
          grade: d.impact >= 20 ? 'high' : d.impact >= 10 ? 'medium' : 'low',
        })),
    [dataPoints],
  );

  const firstSixDataPoints = useMemo(() => {
    return gradedImpactDataPoint.slice(0, 6);
  }, [gradedImpactDataPoint]);

  const additionalFactorsDataPoints = useMemo(() => {
    return gradedImpactDataPoint.slice(6);
  }, [gradedImpactDataPoint]);

  const navigateToTrait = useCallback(
    (dataPoint: KeyFactorsImpactDataPoint) => {
      handleSelectTrait(dataPoint.controlVariableColumnHeaderValue);
    },
    [handleSelectTrait],
  );

  const [showAdditionalFactorsModal, setShowAdditionalFactorsModal] =
    useState(false);

  const metric = useMemo(() => {
    for (const focusArea of myObservation?.focusAreas ?? []) {
      for (const metric of focusArea.metrics) {
        if (metric.columnHeaderValue === metricColumnHeaderValue) {
          return metric;
        }
      }
    }
  }, [metricColumnHeaderValue, myObservation]);

  const getImpactPointDescription = useCallback(
    (dataPoint: KeyFactorsImpactDataPoint) => {
      if (!myObservation) {
        return '';
      }

      const impactPointTrait = traits.find(
        (t) =>
          t.controlVariableColumnHeaderValue ===
          dataPoint.controlVariableColumnHeaderValue,
      );

      if (!impactPointTrait) {
        return '';
      }

      const targetRange = getTargetRangeDisplayForKeyFactors({
        trait: impactPointTrait,
        myObservation,
      });

      const breakdownExplanation = getBreakdownExplanationForKeyFactors({
        trait: impactPointTrait,
        explanations,
        myObservation,
        language,
        metric: metric?.columnHeaderValue ?? '',
        targetRange: targetRange ?? '',
      });
      return breakdownExplanation.overview;
    },
    [explanations, language, metric, myObservation, traits],
  );

  const controlVariables = getControlVariableDefinitionsMapping({
    myObservation,
  });

  const getControlVariableDescription = useCallback(
    (dataPoint: KeyFactorsImpactDataPoint) => {
      if (dataPoint.controlVariableColumnHeaderValue in controlVariables) {
        const controlVariable =
          controlVariables[dataPoint.controlVariableColumnHeaderValue];

        if (controlVariable.description) {
          return controlVariable.description;
        }
      }

      return '';
    },
    [controlVariables],
  );

  const handleAccordionChange = useCallback(
    (index: number, expanded: boolean) => setIsTraitHovered(index, expanded),
    [setIsTraitHovered],
  );

  return (
    <Card
      data-testid="optimizing-card"
      paddingClassName="px-10"
      className="w-full @5xl:max-w-[410px] py-10 h-max-content"
    >
      <div className="min-h-[625px]">
        <Spacing direction="vertical" size="sm">
          <S2Typography
            variant={'headline-3'}
            textColorVariant="royal-800"
            translatedContent={metric?.displayName ?? ''}
          />
          <S2Typography
            variant={'body-6'}
            textColorVariant="gray-800"
            translatedContent={language.keyFactorsOptimizingCardBasedOn({
              metricName: metric?.displayName ?? '',
              calibrationFraming: metric?.calibrationFraming ?? 'Positive',
              subjectPlural: metric?.whiteLabeling.subjectPlural ?? 'employees',
            })}
          />
          <hr className="border-0 border-t border-[#E7E8E8] mb-1 mt-1" />
          {errorStateReason && (
            <S2Typography
              variant={'body-6'}
              textColorVariant="gray-800"
              translatedContent={
                errorStateReason === 'noFactors'
                  ? language.keyFactorsOptimizingCardNoFactors()
                  : errorStateReason === 'notEnoughData'
                    ? language.keyFactorsOptimizingCardNotEnoughData()
                    : ''
              }
            />
          )}
          {!errorStateReason &&
            firstSixDataPoints.map((d, i) => (
              <BorderlessAccordion
                key={i}
                elevation={0}
                disableGutters={true}
                onChange={(_, expanded) => handleAccordionChange(i, expanded)}
                expanded={expandAllTraitsForReport}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className="p-0"
                >
                  <OptimizingCardTopFactor dataPoint={d} />
                </AccordionSummary>
                <AccordionDetails className="p-0">
                  <Spacing direction="vertical" size="gap-3">
                    <S2Typography
                      variant={'body-6'}
                      textColorVariant="gray-900"
                      translatedContent={getControlVariableDescription(d)}
                    />
                    <S2Typography
                      variant={'body-6'}
                      textColorVariant="gray-900"
                      translatedContent={getImpactPointDescription(d)}
                    />

                    <TouchTarget
                      className={'inline-block w-fit'}
                      content={() => (
                        <S2Typography
                          variant={'body-6-bold'}
                          textColorVariant="royal-800"
                          translatedContent={language.keyFactorsOptimizingCardViewBreakdown()}
                        />
                      )}
                      behavior={{
                        type: 'onClick',
                        onClick: () => navigateToTrait(d),
                      }}
                    />
                  </Spacing>
                </AccordionDetails>
              </BorderlessAccordion>
            ))}
          {!errorStateReason && additionalFactorsDataPoints.length > 0 && (
            <TouchTarget
              className={'inline-flex flex-col'}
              content={() => (
                <S2Typography
                  variant={'body-6-bold'}
                  textColorVariant="royal-800"
                  translatedContent={language.keyFactorsOptimizingCardAdditionalFactors()}
                />
              )}
              behavior={{
                type: 'onClick',
                onClick: () => setShowAdditionalFactorsModal(true),
              }}
            />
          )}
          {!errorStateReason && (
            <>
              <hr className="border-0 border-t border-[#E7E8E8] my-1" />
              <p>
                <S2Typography
                  variant={'body-7'}
                  as="span"
                  textColorVariant="gray-800"
                  translatedContent={language.keyFactorsOptimizingCardFooterBlurb()}
                />{' '}
                <TouchTarget
                  className={'inline-flex flex-col'}
                  content={() => (
                    <S2Typography
                      variant={'headline-7-alt'}
                      as="span"
                      textColorVariant="royal-600"
                      translatedContent={language.keyFactorsOptimizingCardFooterTryCopilot()}
                    />
                  )}
                  behavior={{
                    type: 'href',
                    href: '/copilot/hiring',
                  }}
                />
              </p>
            </>
          )}
        </Spacing>
      </div>
      <Modal
        show={showAdditionalFactorsModal}
        onClose={() => setShowAdditionalFactorsModal(false)}
        title={
          <div>
            <S2Typography
              variant={'headline-3-alt'}
              textColorVariant={'royal-800'}
              translatedContent={'Additional Factors'}
              dangerouslyOverrideClassNameFn={(cn) => `${cn} font-medium`}
            />
            <div className="mt-1 pb-2">
              <S2Typography
                variant={'body-6'}
                textColorVariant={'gray-800'}
                translatedContent={
                  '16 factors used in this analysis are displayed below'
                }
              />
            </div>
            <hr className="border-0 border-t border-[#E7E8E8] my-4" />
          </div>
        }
        scrollable={true}
        headerBackgroundOverride="white"
        headerPaddingClassname="pt-8 px-10"
        headerClassname="relative"
        containerClassname="max-w-[461px] max-h-[648px]"
        childrenPadding="pb-8 px-10"
        closeButtonClassname="absolute top-[12px] right-[12px]"
        closeIconOverrideClassname="w-8 h-8 text-gray-900"
      >
        <Spacing direction={'vertical'} size={'gap-3'}>
          <S2Typography
            variant={'body-6'}
            textColorVariant={'gray-800'}
            translatedContent={`Top Factors (${firstSixDataPoints.length})`}
          />
          {firstSixDataPoints.map((d, i) => (
            <OptimizingCardTopFactor key={i} dataPoint={d} />
          ))}
          <hr className="border-0 border-t border-[#E7E8E8] my-1" />
          <S2Typography
            variant={'body-6'}
            textColorVariant={'gray-800'}
            translatedContent={`Additional Factors (${additionalFactorsDataPoints.length})`}
          />
          {additionalFactorsDataPoints.map((d) => (
            <S2Typography
              key={d.controlVariableColumnHeaderValue}
              variant={'body-6-bold'}
              textColorVariant={'gray-800'}
              translatedContent={d.keyFactorImpactLabel}
            />
          ))}
        </Spacing>
      </Modal>
    </Card>
  );
};
